// Additional sections for the Portfolio component
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle2, ArrowRight } from 'lucide-react';

export const AchievementsSection = () => (
  <section id="achievements" className="py-20 px-6 bg-slate-50">
    <div className="max-w-7xl mx-auto">
      <div className="mb-16">
        <div className="text-sm font-semibold text-blue-600 mb-4 tracking-wide uppercase">03 — Key Achievements</div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Business Impact Record</h2>
      </div>

      <div className="space-y-12">
        {/* Cisco Achievement */}
        <Card className="border-slate-200 shadow-md hover:shadow-xl transition-shadow">
          <CardHeader className="border-b border-slate-100 bg-slate-50">
            <div className="flex items-center justify-between mb-4">
              <Badge className="bg-blue-600 text-white">Cisco</Badge>
              <span className="text-sm text-slate-600">Product Manager · 2022 – Present</span>
            </div>
            <CardTitle className="text-2xl md:text-3xl text-slate-900 leading-tight">
              Building the AI-Powered CX Intelligence Platform for a $24B+ Renewal Portfolio
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">30%</div>
                <div className="text-sm text-slate-600 mt-1">Less Manual Analysis</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">10%</div>
                <div className="text-sm text-slate-600 mt-1">Forecast Accuracy Gain</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">$24B+</div>
                <div className="text-sm text-slate-600 mt-1">Portfolio Supported</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded-lg">
                <div className="text-3xl font-bold text-blue-600">Top</div>
                <div className="text-sm text-slate-600 mt-1">Feature Adoption Rank</div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Challenge</h4>
                <p className="text-slate-700 leading-relaxed">
                  Customer success teams managing billions in enterprise renewals were relying on manual analysis, gut-feel forecasting, 
                  and reactive workflows — creating significant revenue risk and operational inefficiency at scale.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Approach</h4>
                <p className="text-slate-700 leading-relaxed">
                  Defined a product vision centred on proactive intelligence: surface the right insight, to the right stakeholder, at the right moment. 
                  Prioritised GenAI capabilities with direct, measurable linkage to renewal outcomes.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Actions Taken</h4>
                <p className="text-slate-700 leading-relaxed">
                  Shipped AI-driven summarisation, churn prediction models, and forecasting features. Developed proactive compliance alerts, 
                  fault detection, and crash-risk signals. Drove adoption through behavioural analytics and workflow integration.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Results Achieved</h4>
                <p className="text-slate-700 leading-relaxed">
                  30% reduction in manual analysis time. 10% improvement in forecasting accuracy. Platform workflows achieved top-tier adoption, 
                  consistently ranking among the most-used features in Cisco CX Cloud.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* VMware Achievement 1 */}
        <Card className="border-slate-200 shadow-md hover:shadow-xl transition-shadow">
          <CardHeader className="border-b border-slate-100 bg-slate-50">
            <div className="flex items-center justify-between mb-4">
              <Badge className="bg-slate-700 text-white">VMware</Badge>
              <span className="text-sm text-slate-600">Product Manager · 2021 – 2022</span>
            </div>
            <CardTitle className="text-2xl md:text-3xl text-slate-900 leading-tight">
              SD-WAN SaaS Automation: Accelerating Onboarding and Subscription Transformation
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-3 gap-8">
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Challenge</h4>
                <p className="text-slate-700 leading-relaxed">
                  Enterprise SD-WAN onboarding was a manual, high-friction process creating delays and poor adoption rates. VMware was also 
                  mid-transformation toward a SaaS subscription model requiring significant process and product change.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Approach</h4>
                <p className="text-slate-700 leading-relaxed">
                  Mapped end-to-end onboarding journeys to identify automation opportunities. Established standardised process frameworks that 
                  could support the SaaS model shift across the product and go-to-market motion.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Business Impact</h4>
                <p className="text-slate-700 leading-relaxed">
                  Improved customer onboarding velocity. Enabled a scalable subscription revenue model. Contributed to a product organisation 
                  managing one of the industry's most complex enterprise SaaS transitions.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* VMware Achievement 2 */}
        <Card className="border-slate-200 shadow-md hover:shadow-xl transition-shadow">
          <CardHeader className="border-b border-slate-100 bg-slate-50">
            <div className="flex items-center justify-between mb-4">
              <Badge className="bg-slate-700 text-white">VMware</Badge>
              <span className="text-sm text-slate-600">Business Analyst II → PM · 2016 – 2021</span>
            </div>
            <CardTitle className="text-2xl md:text-3xl text-slate-900 leading-tight">
              Operationalising Agile Across Global Support, Education & Professional Services
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Challenge</h4>
                <p className="text-slate-700 leading-relaxed">
                  Large global enterprise initiatives were operating without clear scope management, consistent success metrics, or structured 
                  sprint prioritisation — leading to delivery risk and misalignment between business and technology teams.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900 mb-2">Actions Taken</h4>
                <p className="text-slate-700 leading-relaxed">
                  Introduced agile methodologies to improve backlog visibility and prioritisation. Managed project scope, timelines, and roadmap 
                  planning across Global Support, Education, and Professional Services. Led user acceptance testing and delivery coordination across global teams.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);

export const CaseStudiesSection = () => (
  <section id="case-studies" className="py-20 px-6 bg-white">
    <div className="max-w-7xl mx-auto">
      <div className="mb-16">
        <div className="text-sm font-semibold text-blue-600 mb-4 tracking-wide uppercase">04 — Case Studies</div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Case Studies</h2>
        <p className="text-xl text-slate-600 max-w-3xl">In-depth analysis of strategic product initiatives with measurable business outcomes.</p>
      </div>

      <div className="space-y-16">
        {/* Case Study 1 */}
        <div className="border-2 border-slate-200 rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 text-white p-8">
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge className="bg-white/20 text-white border-0">GenAI</Badge>
              <Badge className="bg-white/20 text-white border-0">Churn Prediction</Badge>
              <Badge className="bg-white/20 text-white border-0">Enterprise SaaS</Badge>
              <Badge className="bg-white/20 text-white border-0">Cisco CX Cloud</Badge>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold leading-tight">
              AI-Powered Churn Intelligence for Enterprise Renewal Management
            </h3>
          </div>
          
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-12 mb-12">
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">1</span>
                  Client / Business Context
                </h4>
                <p className="text-slate-700 leading-relaxed">
                  Cisco CX Cloud — a customer success and experience platform supporting a $24B+ annual renewal portfolio across thousands of 
                  enterprise customers globally. Customer Success Managers and Renewal Managers were the primary users, operating at high velocity 
                  with complex account portfolios.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <span className="h-8 w-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">2</span>
                  Problem Statement
                </h4>
                <p className="text-slate-700 leading-relaxed">
                  Despite having large volumes of customer data, the renewal team was operating reactively — spending excessive time on manual 
                  data aggregation, lacking predictive signals for at-risk accounts, and making renewal forecasts based on historical patterns rather 
                  than real-time intelligence. The risk: missed renewals, inaccurate revenue forecasting, and CSM burnout from low-value analysis tasks.
                </p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-8 mb-12">
              <h4 className="text-lg font-semibold text-slate-900 mb-3">Product Vision</h4>
              <p className="text-slate-700 leading-relaxed text-lg">
                An intelligent CX assistant that proactively surfaces the right insight at the right moment — transforming Cisco's customer success 
                motion from reactive firefighting to predictive, data-driven account management. Every CSM should feel like they have an AI analyst 
                working beside them.
              </p>
            </div>

            <div className="space-y-8 mb-12">
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-3">Strategy Developed</h4>
                <p className="text-slate-700 leading-relaxed">
                  Prioritised a three-horizon AI roadmap: (1) GenAI summarisation to eliminate manual data consolidation, (2) ML-driven churn prediction 
                  to flag at-risk accounts before renewal windows, and (3) intelligent forecasting to give leadership reliable revenue visibility. Each 
                  capability was tied to a specific, measurable CSM workflow pain point with clear pre/post success criteria.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-3">Execution Approach</h4>
                <p className="text-slate-700 leading-relaxed">
                  Worked in close partnership with data science, engineering, and analytics teams to define model inputs, output design, and UX integration 
                  patterns. Ran iterative releases with embedded feedback loops from CSM power users. Designed the product to sit within existing workflows 
                  rather than create new ones — minimising adoption friction and maximising usage rates.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-3">Cross-functional Leadership</h4>
                <p className="text-slate-700 leading-relaxed">
                  Aligned engineering, analytics, data science, design, and business stakeholders across multiple geographies. Managed trade-offs between 
                  model accuracy and time-to-market. Built executive-level reporting to secure continued investment in the AI programme.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="text-4xl font-bold text-blue-600 mb-2">30%</div>
                <div className="text-sm text-slate-600">Reduction in manual analysis time</div>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="text-4xl font-bold text-blue-600 mb-2">10%</div>
                <div className="text-sm text-slate-600">Improvement in forecast accuracy</div>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="text-4xl font-bold text-blue-600 mb-2">#1</div>
                <div className="text-sm text-slate-600">Feature adoption ranking</div>
              </div>
              <div className="text-center p-6 bg-blue-50 rounded-xl">
                <div className="text-4xl font-bold text-blue-600 mb-2">$24B+</div>
                <div className="text-sm text-slate-600">Renewal portfolio supported</div>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <h4 className="font-semibold text-slate-900 mb-2">Lesson Learned</h4>
              <p className="text-slate-700 leading-relaxed">
                In enterprise AI products, trust is the primary adoption barrier — not capability. The most impactful design decision was showing users 
                <em> why</em> the model flagged an account, not just <em>that</em> it did. Explainability drove adoption more than accuracy improvements.
              </p>
            </div>
          </div>
        </div>

        {/* Case Study 2 */}
        <div className="border-2 border-slate-200 rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-slate-700 to-slate-800 text-white p-8">
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge className="bg-white/20 text-white border-0">Risk Detection</Badge>
              <Badge className="bg-white/20 text-white border-0">Compliance</Badge>
              <Badge className="bg-white/20 text-white border-0">Customer Health</Badge>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold leading-tight">
              Proactive Compliance & Risk Alerting Platform for Enterprise Customer Health
            </h3>
          </div>
          
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-3">Problem Statement</h4>
                <p className="text-slate-700 leading-relaxed">
                  Cisco's customer-facing teams lacked the tooling to proactively identify and communicate risk to customers before incidents occurred. 
                  Fault detection was reactive, compliance tracking was manual, and crash-risk signals were buried in telemetry data.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-3">Product Vision</h4>
                <p className="text-slate-700 leading-relaxed">
                  Transform Cisco from a vendor customers call after something breaks, to a partner that helps customers prevent problems entirely — 
                  building a platform that systematically identifies risk and surfaces it to the right person with clear recommended actions.
                </p>
              </div>
            </div>

            <div className="mb-8">
              <h4 className="text-lg font-semibold text-slate-900 mb-3">Strategy Developed</h4>
              <p className="text-slate-700 leading-relaxed">
                Designed a proactive alerting framework within CX Cloud covering three pillars: compliance gap detection, fault pattern identification, 
                and crash-risk scoring. Prioritised use cases by signal strength and revenue risk, starting with the highest-impact alerts that could 
                demonstrably improve customer health scores.
              </p>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <h4 className="font-semibold text-slate-900 mb-2">Lesson Learned</h4>
              <p className="text-slate-700 leading-relaxed">
                Alert design is a product discipline in itself. An alert that fires too often is ignored; one that fires too rarely loses trust. 
                The critical PM skill was working with data science to define precision/recall trade-offs in product terms, not statistical ones.
              </p>
            </div>
          </div>
        </div>

        {/* Case Study 3 */}
        <div className="border-2 border-slate-200 rounded-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-slate-600 to-slate-700 text-white p-8">
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge className="bg-white/20 text-white border-0">SaaS</Badge>
              <Badge className="bg-white/20 text-white border-0">Automation</Badge>
              <Badge className="bg-white/20 text-white border-0">Onboarding</Badge>
              <Badge className="bg-white/20 text-white border-0">VMware</Badge>
            </div>
            <h3 className="text-3xl md:text-4xl font-bold leading-tight">
              SD-WAN SaaS Automation: Compressing Enterprise Onboarding at Scale
            </h3>
          </div>
          
          <div className="p-8">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-3">Business Context</h4>
                <p className="text-slate-700 leading-relaxed">
                  VMware's SD-WAN product served large enterprise clients with complex multi-site networking needs. VMware was simultaneously executing 
                  a business model transition toward SaaS subscriptions — a transformation that required rebuilding operational processes, pricing models, 
                  and product workflows from the ground up.
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-3">Problem Statement</h4>
                <p className="text-slate-700 leading-relaxed">
                  Enterprise SD-WAN onboarding was slow, manual, and inconsistent — creating a negative first experience that undermined product adoption 
                  and increased implementation costs. Simultaneously, the SaaS transition lacked standardised processes to enable a scalable subscription motion.
                </p>
              </div>
            </div>

            <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-r-lg">
              <h4 className="font-semibold text-slate-900 mb-2">Lesson Learned</h4>
              <p className="text-slate-700 leading-relaxed">
                SaaS transformation is as much a change management challenge as a product one. The most valuable PM contribution was not just the product work, 
                but building the shared language between product, sales, and ops teams about what "subscription-ready" actually meant in practice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const CapabilitiesSection = () => (
  <section id="capabilities" className="py-20 px-6 bg-slate-50">
    <div className="max-w-7xl mx-auto">
      <div className="mb-16">
        <div className="text-sm font-semibold text-blue-600 mb-4 tracking-wide uppercase">05 — Product Leadership Capabilities</div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Skills & Competencies</h2>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          {
            title: 'Product Strategy',
            description: 'Vision definition, competitive positioning, market sizing, strategic prioritisation, and opportunity framing for boards and investors.'
          },
          {
            title: 'Roadmap Planning',
            description: 'Outcome-based roadmaps, Now/Next/Later frameworks, dependency mapping, and stakeholder alignment across engineering, design, and business.'
          },
          {
            title: 'AI Product Development',
            description: 'GenAI use case identification, prompt engineering, AI feature UX, model evaluation, responsible AI principles, and AI adoption measurement.'
          },
          {
            title: 'User Research',
            description: 'Customer interviews, Jobs-to-be-Done analysis, persona development, journey mapping, usability testing, and insight synthesis.'
          },
          {
            title: 'Data Analytics',
            description: 'Behavioural analytics, funnel analysis, feature adoption metrics, churn modelling, and product experimentation design.'
          },
          {
            title: 'Stakeholder Management',
            description: 'Executive communication, cross-functional alignment, OKR definition, board-level reporting, and building stakeholder confidence in product decisions.'
          },
          {
            title: 'Agile Delivery',
            description: 'Certified Scrum Master with experience running high-performing product squads. Sprint planning, backlog refinement, retrospectives, and delivery metrics.'
          },
          {
            title: 'GTM Planning',
            description: 'Launch strategy, positioning, messaging, sales enablement, feature adoption design, and channel strategy for enterprise B2B products.'
          },
          {
            title: 'Customer Experience',
            description: 'End-to-end CX platform design, customer health frameworks, proactive engagement models, and CX metrics definition.'
          },
          {
            title: 'Growth & Retention',
            description: 'Renewal optimisation, churn prediction, activation and engagement loop design, upsell intelligence, and expansion revenue frameworks.'
          }
        ].map((capability, idx) => (
          <Card key={idx} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="text-xl text-slate-900">{capability.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 leading-relaxed text-sm">{capability.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

export const WhyWorkSection = () => (
  <section id="why-work" className="py-20 px-6 bg-white">
    <div className="max-w-7xl mx-auto">
      <div className="mb-16">
        <div className="text-sm font-semibold text-blue-600 mb-4 tracking-wide uppercase">06 — Why Work With Me</div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">The Difference</h2>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {[
          {
            icon: '◈',
            title: 'Strategic Mindset, Not Feature Factory',
            description: "I operate at the level of business outcomes, not feature lists. Every engagement begins with understanding the commercial problem you're trying to solve — then working backwards to the product decisions that will actually move the needle. I've owned roadmaps at billion-dollar revenue scale and know the difference between activity and impact."
          },
          {
            icon: '◇',
            title: 'AI Fluency — Technical and Strategic',
            description: "I've shipped production GenAI features in enterprise environments where accuracy, trust, and explainability are non-negotiable. I understand both the strategic opportunity and the practical reality of AI product development — from prompt design to adoption measurement. I'll help you cut through the hype and build AI that delivers real business value."
          },
          {
            icon: '○',
            title: 'Execution Excellence at Enterprise Scale',
            description: "I've navigated the complexity of shipping products inside two of the world's most demanding enterprise technology organisations. I know how to manage stakeholders, make decisions under ambiguity, prioritise ruthlessly when resources are constrained, and deliver high-quality work in environments where the stakes are real."
          },
          {
            icon: '□',
            title: 'Customer-Centric by Default',
            description: "Every product decision I make is grounded in a clear understanding of who the customer is, what job they're trying to do, and what success looks like from their perspective. I've spent years building products for enterprise users with sophisticated needs — and I bring that rigour to every engagement, regardless of company size."
          },
          {
            icon: '◉',
            title: 'Founder and Executive Communication',
            description: "I communicate product strategy in the language of business outcomes, not feature descriptions. I'm comfortable in board rooms, engineering standups, and customer interviews with equal fluency. For founders and growth-stage leaders, that means a partner who can hold their own in any room your business requires."
          },
          {
            icon: '◆',
            title: 'Range: Enterprise Rigour, Startup Speed',
            description: "I've operated inside the structured, high-accountability environment of Cisco and VMware — and I've seen what happens when that rigour is applied with startup urgency. I bring frameworks that scale, but I know when to discard process in favour of speed. That balance is exactly what growth-stage companies need in a product partner."
          }
        ].map((item, idx) => (
          <Card key={idx} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-start gap-4">
                <div className="text-3xl text-blue-600 font-light">{item.icon}</div>
                <CardTitle className="text-2xl text-slate-900 leading-tight">{item.title}</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-slate-600 leading-relaxed">{item.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  </section>
);

export const EngagementSection = () => (
  <section id="engagement" className="py-20 px-6 bg-slate-50">
    <div className="max-w-7xl mx-auto">
      <div className="mb-16">
        <div className="text-sm font-semibold text-blue-600 mb-4 tracking-wide uppercase">07 — Engagement Models</div>
        <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Consulting Packages</h2>
        <p className="text-xl text-slate-600 max-w-3xl">
          All packages are customisable. Reach out to discuss your needs and receive a tailored proposal.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        <Card className="border-slate-200 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <CardHeader className="border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white">
            <div className="text-sm font-semibold text-blue-600 mb-2 uppercase tracking-wide">Diagnostic</div>
            <CardTitle className="text-2xl text-slate-900 mb-4">Product Audit</CardTitle>
            <div className="text-sm text-slate-600 font-medium">2 – 3 Weeks</div>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="space-y-3 mb-6">
              {[
                'Product-market fit assessment',
                'Feature adoption & value gap analysis',
                'Roadmap coherence review',
                'Retention & churn diagnostic',
                'Competitive positioning review',
                'Executive report + 90-day action plan'
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Button 
              data-testid="package-product-audit-btn"
              onClick={() => window.location.href = 'mailto:lopa.misra@gmail.com?subject=Product Audit Inquiry'}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Start Audit
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <CardHeader className="border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white">
            <div className="text-sm font-semibold text-blue-600 mb-2 uppercase tracking-wide">Sprint</div>
            <CardTitle className="text-2xl text-slate-900 mb-4">Product Strategy Sprint</CardTitle>
            <div className="text-sm text-slate-600 font-medium">4 – 6 Weeks</div>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="space-y-3 mb-6">
              {[
                'Market & customer opportunity analysis',
                '12-month prioritised roadmap',
                'OKR & success metrics framework',
                'Strategic narrative development',
                'Stakeholder alignment workshops',
                'Investor / board-ready deliverable'
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Button 
              data-testid="package-strategy-sprint-btn"
              onClick={() => window.location.href = 'mailto:lopa.misra@gmail.com?subject=Product Strategy Sprint Inquiry'}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Enquire
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-600 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 relative">
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <Badge className="bg-blue-600 text-white px-4 py-1">Most Popular</Badge>
          </div>
          <CardHeader className="border-b border-slate-100 bg-gradient-to-br from-blue-50 to-white">
            <div className="text-sm font-semibold text-blue-600 mb-2 uppercase tracking-wide">Embedded</div>
            <CardTitle className="text-2xl text-slate-900 mb-4">Fractional Product Manager</CardTitle>
            <div className="text-sm text-slate-600 font-medium">3-month minimum engagement</div>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="space-y-3 mb-6">
              {[
                '2–3 days per week embedded',
                'Full roadmap and backlog ownership',
                'Sprint leadership and delivery management',
                'Stakeholder and investor communication',
                'Hiring and team-building advisory',
                'Weekly executive check-ins'
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Button 
              data-testid="package-fractional-pm-btn"
              onClick={() => window.location.href = 'mailto:lopa.misra@gmail.com?subject=Fractional Product Manager Inquiry'}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Let's Talk
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
          <CardHeader className="border-b border-slate-100 bg-gradient-to-br from-slate-50 to-white">
            <div className="text-sm font-semibold text-blue-600 mb-2 uppercase tracking-wide">Advisory</div>
            <CardTitle className="text-2xl text-slate-900 mb-4">AI Product Advisory</CardTitle>
            <div className="text-sm text-slate-600 font-medium">Ongoing retainer</div>
          </CardHeader>
          <CardContent className="pt-6">
            <ul className="space-y-3 mb-6">
              {[
                '2 × monthly strategy sessions',
                'AI use case identification & scoping',
                'GenAI product design review',
                'AI adoption measurement framework',
                'Async Q&A and Slack access',
                'Intro to relevant network'
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <Button 
              data-testid="package-ai-advisory-btn"
              onClick={() => window.location.href = 'mailto:lopa.misra@gmail.com?subject=AI Product Advisory Inquiry'}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Explore
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  </section>
);
